import React, { useState } from "react";
import styles from "./Contact.module.css";
import { EMAIL, MAIL_LINK, WHATSAPP_LINK, WHATSAPP_DISPLAY, CALL_LINK } from "../siteData";

const SERVICE_OPTIONS = [
  "General Cleaning",
  "Deep Cleaning",
  "Post-Renovation Cleaning",
  "Plumbing",
  "Electrical Work",
  "Painting",
  "Kitchen / Bathroom Renovation",
  "Installation & Repair",
  "Other / Not Sure Yet",
];

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);

  // This form posts to FormSubmit (https://formsubmit.co), a free form-to-email
  // service that needs no backend code. The first submission sends a one-time
  // confirmation link to the inbox above — click it once to activate the form,
  // then future submissions arrive straight to the inbox.
  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(false);
    setSending(true);

    const form = event.target;
    const data = new FormData(form);

    try {
      const response = await fetch(`https://formsubmit.co/ajax/${EMAIL}`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: data,
      });

      if (!response.ok) throw new Error("Request failed");
      setSubmitted(true);
      form.reset();
    } catch (err) {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className={styles.section}>
      <div className="container">
        <div className={styles.heading}>
          <p className="jc-eyebrow">Get In Touch</p>
          <h2 className={styles.title}>Tell us about the job</h2>
          <p className={styles.desc}>
            Send a few details and we'll get back to you, or skip straight to
            WhatsApp for a faster reply.
          </p>
        </div>

        <div className={`${styles.grid}`}>
          <div className={styles.infoCard}>
            <h3 className={styles.infoTitle}>Contact details</h3>

            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className={styles.infoRow}>
              <span className={styles.infoIcon}>
                <WhatsAppIcon />
              </span>
              <span>
                <span className={styles.infoLabel}>WhatsApp / Call</span>
                <span className={styles.infoValue}>{WHATSAPP_DISPLAY}</span>
              </span>
            </a>

            <a href={MAIL_LINK} className={styles.infoRow}>
              <span className={styles.infoIcon}>
                <MailIcon />
              </span>
              <span>
                <span className={styles.infoLabel}>Email</span>
                <span className={styles.infoValue}>{EMAIL}</span>
              </span>
            </a>

            <a href={CALL_LINK} className={styles.infoRow}>
              <span className={styles.infoIcon}>
                <PhoneIcon />
              </span>
              <span>
                <span className={styles.infoLabel}>Direct Call</span>
                <span className={styles.infoValue}>{WHATSAPP_DISPLAY}</span>
              </span>
            </a>

            <div className={styles.infoDivider} />
            <p className={styles.infoNote}>
              Serving homes, offices, shops and other properties. Message us
              anytime — we reply as soon as we can.
            </p>
          </div>

          <div className={styles.formCard}>
            {submitted ? (
              <div className={styles.successState}>
                <div className={styles.successIcon}>✓</div>
                <h3>Thanks — message sent</h3>
                <p>
                  We've received your details and will get back to you soon.
                  For a faster reply, message us directly on WhatsApp.
                </p>
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noreferrer"
                  className="jc-btn jc-btn-primary"
                >
                  Chat on WhatsApp
                </a>
              </div>
            ) : (
              <form
                action={`https://formsubmit.co/${EMAIL}`}
                method="POST"
                onSubmit={handleSubmit}
                className={styles.form}
              >
                <input type="hidden" name="_subject" value="New enquiry from website" />
                <input type="hidden" name="_template" value="table" />
                <input type="hidden" name="_captcha" value="false" />
                {/* Honeypot field to reduce spam */}
                <input type="text" name="_honey" className={styles.honey} tabIndex="-1" autoComplete="off" />

                <div className="row g-3">
                  <div className="col-12 col-sm-6">
                    <label className={styles.label} htmlFor="name">
                      Full name
                    </label>
                    <input
                      className={styles.input}
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="Your name"
                    />
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className={styles.label} htmlFor="phone">
                      Phone / WhatsApp
                    </label>
                    <input
                      className={styles.input}
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder="e.g. 011-3309 2064"
                    />
                  </div>
                  <div className="col-12">
                    <label className={styles.label} htmlFor="email">
                      Email
                    </label>
                    <input
                      className={styles.input}
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                    />
                  </div>
                  <div className="col-12">
                    <label className={styles.label} htmlFor="service">
                      Service needed
                    </label>
                    <select className={styles.input} id="service" name="service" defaultValue="">
                      <option value="" disabled>
                        Choose a service
                      </option>
                      {SERVICE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className={styles.label} htmlFor="message">
                      Tell us about the job
                    </label>
                    <textarea
                      className={styles.textarea}
                      id="message"
                      name="message"
                      rows="4"
                      required
                      placeholder="Property type, size, location and what you need done"
                    />
                  </div>
                </div>

                {error && (
                  <p className={styles.errorNote}>
                    Something went wrong sending the form. Please try again,
                    or message us directly on{" "}
                    <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer">
                      WhatsApp
                    </a>{" "}
                    or by{" "}
                    <a href={MAIL_LINK}>email</a>.
                  </p>
                )}

                <button
                  type="submit"
                  className={`jc-btn jc-btn-primary ${styles.submit}`}
                  disabled={sending}
                >
                  {sending ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatsAppIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.04 2c-5.5 0-9.96 4.46-9.96 9.96 0 1.76.46 3.48 1.34 4.99L2 22l5.2-1.36a9.9 9.9 0 0 0 4.84 1.24h.01c5.5 0 9.96-4.46 9.96-9.96S17.54 2 12.04 2Zm5.83 14.24c-.25.7-1.24 1.28-2.03 1.44-.55.11-1.26.2-3.66-.79-2.86-1.19-4.79-3.98-4.94-4.16-.14-.19-1.19-1.58-1.19-3.01 0-1.43.75-2.13 1.02-2.42.25-.29.57-.36.76-.36.19 0 .38 0 .55.01.18.01.42-.07.65.5.25.6.85 2.07.92 2.22.07.15.12.32.02.51-.1.19-.15.31-.29.48-.14.17-.3.37-.43.5-.14.14-.29.29-.13.57.16.28.72 1.19 1.55 1.93 1.06.94 1.96 1.24 2.24 1.38.28.14.44.12.6-.07.17-.19.72-.83.91-1.12.19-.28.38-.24.64-.14.26.1 1.66.78 1.94.92.28.14.47.21.53.33.07.12.07.68-.18 1.38Z" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.4 21 3 13.6 3 4.5a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.24.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2Z" />
    </svg>
  );
}
